
interface AuthState {
  accessToken: string | null;
  preferredUsername: string | null;
  idToken: string | null;
  expiresAt: number | null;
}

let authState: AuthState = {
  accessToken: null,
  preferredUsername: null,
  idToken: null,
  expiresAt: null,
};

interface AuthStateChange {
  sessionConfirmed: boolean;
}

type AuthListener = (change: AuthStateChange) => void;

const authListeners = new Set<AuthListener>();

interface SetAuthStateOptions {
  sessionConfirmed?: boolean;
}

function notifyAuthListeners(sessionConfirmed: boolean): void {
  authListeners.forEach((listener) => listener({ sessionConfirmed }));
}

export function subscribeAuthState(listener: AuthListener): () => void {
  authListeners.add(listener);
  return () => authListeners.delete(listener);
}

export function getAuthStateSnapshot(): AuthState {
  return authState;
}

export function setAuthState(
  state: Partial<AuthState>,
  options: SetAuthStateOptions = {},
): void {
  const sessionConfirmed = options.sessionConfirmed ?? false;
  const nextState = { ...authState, ...state };
  const isSameState = nextState.accessToken === authState.accessToken
    && nextState.preferredUsername === authState.preferredUsername
    && nextState.idToken === authState.idToken
    && nextState.expiresAt === authState.expiresAt;
  if (isSameState) {
    if (sessionConfirmed) {
      notifyAuthListeners(sessionConfirmed);
    }
    return;
  }
  authState = nextState;
  notifyAuthListeners(sessionConfirmed);
}

export function getAccessTokenOrNull(): string | null {
  return authState.accessToken;
}

export function getPreferredUsername(): string | null {
  return authState.preferredUsername;
}

export function clearAuthState(): void {
  const isAlreadyCleared = authState.accessToken === null
    && authState.preferredUsername === null
    && authState.idToken === null
    && authState.expiresAt === null;
  if (isAlreadyCleared) return;
  authState = {
    accessToken: null,
    preferredUsername: null,
    idToken: null,
    expiresAt: null,
  };
  notifyAuthListeners(false);
}
